// Short movement for the moments that finish something (Liam, 2026-09-24:
// "love the animations can we add them anywhere else"). Kept to a few places
// on purpose: finishing a job, marking a job paid on Owed, moving a job on the
// diary. Every helper does nothing on a phone set to reduce motion, and each
// leaves the page exactly as it would have been without it.

export function reducedMotion(): boolean {
  return typeof matchMedia === 'function' && matchMedia('(prefers-reduced-motion: reduce)').matches
}

function canAnimate(node: Element): boolean {
  return !reducedMotion() && typeof (node as HTMLElement).animate === 'function'
}

// A figure that moves from one value to the next. `text` draws the words for
// a point between 0 (the old value) and 1 (the new); the last frame is
// always text(1).
export function countBetween(node: HTMLElement, text: (k: number) => string, ms = 650): void {
  if (reducedMotion() || typeof requestAnimationFrame !== 'function') {
    node.textContent = text(1)
    return
  }
  const t0 = performance.now()
  node.textContent = text(0)
  // The clock is read on each frame rather than taken from the frame's own
  // timestamp, and a timer lands the final figure even if frames stop coming
  // (a phone that locks mid-count): the number shown must end up exact.
  const step = (): void => {
    const k = Math.max(0, Math.min(1, (performance.now() - t0) / ms))
    node.textContent = text(1 - Math.pow(1 - k, 3))
    if (k < 1 && node.isConnected) requestAnimationFrame(step)
    else node.textContent = text(1)
  }
  requestAnimationFrame(step)
  window.setTimeout(() => {
    node.textContent = text(1)
  }, ms + 250)
}

// A row leaves: it slides to the right as it fades, then the space it held
// closes up. Resolves when it is gone (at once without motion).
export function slideAway(node: HTMLElement, delayMs = 0): Promise<void> {
  if (!canAnimate(node)) return Promise.resolve()
  const cs = getComputedStyle(node)
  const ease = 'cubic-bezier(.4,0,.2,1)'
  // The movement set's structural timing (docs/ANIMATION-STANDARD.md): out in 180ms, the gap shut in 180ms.
  const out = node.animate([{ transform: 'none', opacity: 1 }, { transform: 'translateX(48px)', opacity: 0 }], { duration: 180, delay: delayMs, easing: ease, fill: 'forwards' })
  return out.finished.then(
    () => {
      node.style.overflow = 'hidden'
      const shut = node.animate(
        [
          { height: `${node.offsetHeight}px`, paddingTop: cs.paddingTop, paddingBottom: cs.paddingBottom, marginTop: cs.marginTop, marginBottom: cs.marginBottom, borderTopWidth: cs.borderTopWidth, borderBottomWidth: cs.borderBottomWidth },
          { height: '0px', paddingTop: '0px', paddingBottom: '0px', marginTop: '0px', marginBottom: '0px', borderTopWidth: '0px', borderBottomWidth: '0px' },
        ],
        { duration: 180, easing: ease, fill: 'forwards' },
      )
      return shut.finished.then(
        () => undefined,
        () => undefined,
      )
    },
    () => undefined,
  )
}

// A group that is finished folds away: it fades as the space it held closes up (Old quotes cleared, Messages, 16 Oct
// 2026). Resolves when it is gone (at once without motion).
export function foldAway(node: HTMLElement): Promise<void> {
  if (!canAnimate(node)) return Promise.resolve()
  const cs = getComputedStyle(node)
  node.style.overflow = 'hidden'
  const shut = node.animate(
    [
      { height: `${node.offsetHeight}px`, opacity: 1, paddingTop: cs.paddingTop, paddingBottom: cs.paddingBottom, marginTop: cs.marginTop, marginBottom: cs.marginBottom, borderTopWidth: cs.borderTopWidth, borderBottomWidth: cs.borderBottomWidth },
      { height: '0px', opacity: 0, paddingTop: '0px', paddingBottom: '0px', marginTop: '0px', marginBottom: '0px', borderTopWidth: '0px', borderBottomWidth: '0px' },
    ],
    { duration: 250, easing: 'cubic-bezier(.4,0,.2,1)', fill: 'forwards' },
  )
  return shut.finished.then(
    () => undefined,
    () => undefined,
  )
}

// A row arrives: one lemon glow that fades back to the row's own colour (the movement set's "land", 450ms).
export function landFlash(node: HTMLElement): void {
  if (!canAnimate(node)) return
  node.animate(
    [
      { boxShadow: '0 0 0 0 rgba(244,220,82,0)' },
      { boxShadow: '0 0 0 4px rgba(244,220,82,1)', offset: 0.3 },
      { boxShadow: '0 0 0 0 rgba(244,220,82,0)' },
    ],
    { duration: 450, easing: 'ease-out' },
  )
}

// A green tick lands on a card that has just been dealt with (a message sent
// on Messages, 2026-09-25). It sits in the card's top corner; the card then
// usually slides away with slideAway, taking the tick with it.
export function tickStamp(node: HTMLElement): void {
  if (!canAnimate(node)) return
  const tick = document.createElement('span')
  tick.className = 'motion-tick'
  tick.setAttribute('aria-hidden', 'true')
  tick.textContent = '✓'
  if (getComputedStyle(node).position === 'static') node.style.position = 'relative'
  node.append(tick)
  tick.animate(
    // The one tick (the movement set): in from a little smaller, 180ms.
    [
      { transform: 'scale(.6)', opacity: 0 },
      { transform: 'none', opacity: 1 },
    ],
    { duration: 180, easing: 'ease-out', fill: 'forwards' },
  )
}

// The next card arriving from the right after one has gone (Messages, Focus mode): the movement set's slide-in, 220ms.
export function slideIn(node: HTMLElement | null): void {
  if (!node || !canAnimate(node)) return
  node.animate([{ transform: 'translateX(40px)', opacity: 0 }, { transform: 'none', opacity: 1 }], { duration: 220, easing: 'cubic-bezier(.2,.8,.2,1)' })
}
